import { Autocomplete, Box, Chip, CircularProgress, FormControl, IconButton, InputAdornment, InputLabel, LinearProgress, MenuItem, OutlinedInput, Select, SelectChangeEvent, SelectProps, SimplePaletteColorOptions, Skeleton, TextField, Typography, useMediaQuery, useTheme, Theme, SxProps } from "@mui/material"
import { ContentWrapper, SnackbarProvider, SnackbarType, TextOverflow, formatDate, formatNumber, formatPercent, formatSeasons, getDaysCountBetweenDates, getYCenterStyles, useChangeEffect, useControlledState, useTask } from "vig-ui-library";
import { IoIosSettings, IoIosTrendingDown, IoIosTrendingUp } from "react-icons/io";
import { LineChart, LineSeriesType } from '@mui/x-charts';
import { StatisticsCard, StatisticsCardProps } from "@/Components/Statistics/StatisticsCard";
import { SliderChart, SliderChartData } from "@/Components/Statistics/SliderChart";
import { StatesCards } from "@/Components/Statistics/StateCardChart";
import { StatisticsInput } from "@/Schemas/Statistics/StatisticsInput";
import { getSourceStatistics, getStatistics } from "@/Api/StatisticsApi";
import { MutableRefObject, useEffect, useState } from "react";
import { StatisticsOutput } from "@/Schemas/Statistics/StatisticsOutput";
import { StatisticsOverallOutput } from "@/Schemas/Statistics/StatisticsOverallOutput";
import { useAppProvider } from "@/Provider/AppProvider";
import { BaseStatisticsOutputItem } from "@/Schemas/Statistics/BaseStatisticsOutputItem";
import { StatisticsProcessingOutput } from "@/Schemas/Statistics/StatisticsProcessingOutput";
import { StatisticsSourceOutput } from "@/Schemas/Statistics/StatisticsSourceOutput";
import { MakeOptional } from "@mui/x-date-pickers/internals";
import { DatePicker, DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import { BaseConfigurationsOutputItem } from "@/Schemas/Configurations/BaseConfigurationsOutputItem";
import { IoPieChartOutline } from "react-icons/io5";
import dayjs from "dayjs";
import { ClearIcon } from "@mui/x-date-pickers";
import { StatisticsSourceInputSchema } from "@/Schemas/Statistics/StatisticsSourceInput";
import { ConfigurationsTypeOutputItem } from "@/Schemas/Configurations/ConfigurationsTypeOutput";

type RelativeFactor = "day" | "month" | "quarter" | "year";

const addOneDay = (date: Date): Date => {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() + 1);
    return newDate;
}


export const Statistics = () => {
    const theme = useTheme();

    const { configurations } = useAppProvider();

    const { openSnackbar } = SnackbarProvider.useSnackbar();

    const today = new Date();

    const currentMonthFirstDay = addOneDay(new Date(today.getFullYear(), today.getMonth(), 1));

    const currentMonthLastDay = addOneDay(new Date(today.getFullYear(), today.getMonth() + 1, 0));

    const [relativeFactorRef, setRelativeFactor] = useControlledState<RelativeFactor>("day");

    const [input, setInput] = useState<StatisticsInput>({
        dateFrom: currentMonthFirstDay,
        dateTo: currentMonthLastDay,
        compare: true,
        stateTypes: [],
        requestTypes: [],
        sourceTypes: [],
        processingTypes: [],
    });

    const { value: statistics, perform, debouncedPerform, isRunning, isInitialCall } = useTask<[], StatisticsOutput | null>(async () => {
        try {
            return await getStatistics(input);
        } catch (err) {
            openSnackbar({
                text: "Nepodarilo sa načítať štatistiky",
                type: SnackbarType.ERROR,
            });
            return null;
        }
    });

    useEffect(() => {
        if (isInitialCall) perform();
    }, []);

    if (isRunning && isInitialCall) return <StatisticsSkeleton />;

    if (!statistics) return <Box sx={{ width: "100%", gap: 2, height: "100%", display: "flex", padding: 2, alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
        <IoPieChartOutline fontSize={40} color={theme.palette.error.main} />

        <Typography variant="body2" textAlign="center" maxWidth="400px">
            Nepodarilo sa načítať štatistiky. Prosím, skúste to znova. Ak problém pretrváva, kontaktujte IT.
        </Typography>
    </Box>;

    const cardsData: StatisticsCardProps[] = (statistics.state?.masterItems || []).map((item: BaseStatisticsOutputItem) => {
        const configurationItem = configurations.state.masterItems.find((configItem) => configItem.id === item.id);
        return {
            accentColor: item.id === 1,
            title: configurationItem?.label || "Neznámy stav",
            count: item.value,
            percentage: item.percentage,
            percentageColor: configurationItem?.color || theme.palette.text.secondary,
        };
    });

    return (
        <ContentWrapper>
            <LinearProgress sx={{
                visibility: (isRunning && !isInitialCall) ? "visible" : "hidden",
                width: "100%",
            }} />

            <StatisticsHeader
                input={input}
                relativeFactorRef={relativeFactorRef}
                setFactorWithoutRender={setRelativeFactor}
                setInput={(newInput) => {
                    setInput(newInput);
                    debouncedPerform();
                }}
            />

            <StyledRequestCountWrapper>
                <TotalCountOfRequests overall={statistics?.overall} />

                <StyledCardsWrapper>
                    <Typography variant="h6" sx={{ paddingX: { xs: 2, md: 0 }, display: { xs: "inline-block", md: "none" } }}>
                        Počet žiadostí podľa statusu
                    </Typography>

                    <StyledCardsScrollableBox>
                        {cardsData.map((item, index) => (
                            <StatisticsCard key={index} {...item} />
                        ))}
                    </StyledCardsScrollableBox>
                </StyledCardsWrapper>
            </StyledRequestCountWrapper>

            <Box sx={{
                width: "100%",
                display: "flex",
                flexWrap: { xs: "wrap", lg: "nowrap" },
                gap: 2,
                paddingX: { xs: 0, md: 2 },
                marginBottom: 10,
            }}>
                <Box sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}>
                    <AutomatizationSliderChart processing={statistics.processing} />

                    <Box sx={{ width: "100%" }}>
                        <SourceChart
                            source={statistics.source}
                            input={input}
                            relativeFactorRef={relativeFactorRef}
                        />
                    </Box>
                </Box>

                <Box sx={{
                    display: "flex",
                    maxWidth: { xs: "100%", lg: 400 },
                    width: "100%",
                    flexShrink: { xs: 1, lg: 0 },
                    paddingX: { xs: 2, md: 0 },
                }}>
                    <StatesCards items={statistics.state?.items || []} />
                </Box>
            </Box>
        </ContentWrapper>
    );
}

export type StatisticsHeaderProps = {
    input: StatisticsInput;
    setInput: (newInput: StatisticsInput) => void;
    relativeFactorRef: MutableRefObject<RelativeFactor>;
    setFactorWithoutRender: (newFactor: RelativeFactor) => void;
}

const getStyles = (id: number, selectetOptions: readonly number[], theme: Theme) => {
    const isSelected = selectetOptions.some((option) => option === id);
    const { fontWeightRegular, fontWeightMedium } = theme.typography;
    return { fontWeight: isSelected ? fontWeightMedium : fontWeightRegular };
}

const StatisticsHeader = ({ input, setInput, relativeFactorRef, setFactorWithoutRender }: StatisticsHeaderProps) => {
    const { configurations } = useAppProvider();

    const theme = useTheme();

    const mapOption = (option: ConfigurationsTypeOutputItem | BaseConfigurationsOutputItem) => ({ value: option.id, label: option.label });

    const [availableRequestTypes, setAvailableRequestTypes] = useControlledState<number[]>([]);

    const processingOptions = configurations.processing.items.map(mapOption);

    const resolveAvailableRequestTypes = (sourceIds: number[]) => {
        if (sourceIds?.length > 0) {
            const sourceItems = configurations.source.items.filter((configItem) => sourceIds.includes(configItem.id));
            setAvailableRequestTypes(Array.from(new Set(sourceItems.flatMap((item) => item.requestTypeConnections ?? []))));
        } else {
            setAvailableRequestTypes([]);
        }
    }

    const typeOptions = configurations.type.items
        .filter((_item) => {
            return availableRequestTypes.current.length === 0 || availableRequestTypes.current.includes(_item.id);
        })
        .map(mapOption);

    const sourceTypesOptions = configurations.source.items.map(mapOption);

    const relativeFactorOptions: { value: RelativeFactor; label: string }[] = [
        { value: "day", label: "Dní" },
        { value: "month", label: "Mesiacov" },
        { value: "quarter", label: "Kvartálov" },
        { value: "year", label: "Rokov" },
    ];

    const selectedFactorOption = relativeFactorOptions.find((option) => option.value === relativeFactorRef.current);

    const { dateFrom: dateFromRaw, dateTo: dateToRaw } = input;

    const dateFrom = dayjs(dateFromRaw);

    const dateTo = dayjs(dateToRaw);

    const today = new Date();

    const lastDateInYear = new Date(today.getFullYear(), 11, 31);

    const dateFromFirstDay = new Date(dateFromRaw.getFullYear(), dateFromRaw.getMonth(), 1);

    const sharableProps: Omit<DatePickerProps<dayjs.Dayjs, false>, 'value' | 'onChange' | 'label'> = {
        sx: {
            width: { xs: "100%", md: 220 },
            ["& .MuiInputBase-root"]: { backgroundColor: "transparent" },
        },
        slotProps: {
            textField: { variant: "filled" },
        },
    }

    const resolveDateTo = (dateFrom: Date): Date => {
        let newDateTo = dateTo.toDate();

        if (relativeFactorRef.current === "day") {
            const lastDayInMonth = new Date(dateFrom.getFullYear(), dateFrom.getMonth() + 1, 0);
            newDateTo = lastDayInMonth;
        } else {
            const lastDayInYear = new Date(dateFrom.getFullYear(), 11, 31);
            newDateTo = lastDayInYear;
        }

        return addOneDay(newDateTo);
    }

    const onDateFromChange = (value: dayjs.Dayjs | null) => {
        if (!value) return;

        if (relativeFactorRef.current === "year") {
            setInput({ ...input, dateFrom: value.toDate() });
            return;
        }

        const newDateTo = resolveDateTo(value.toDate());

        setInput({ ...input, dateFrom: value.toDate(), dateTo: newDateTo });
    }

    const onFactorChange = (newFactor: RelativeFactor) => {
        if (!selectedFactorOption) return;
        setFactorWithoutRender(newFactor);
        const newDateTo = resolveDateTo(dateFromRaw);

        if (newFactor === "year") {
            const previousYear = new Date(dateFromRaw.getFullYear() - 1, 0, 1);
            setInput({ ...input, dateFrom: addOneDay(previousYear), dateTo: newDateTo });
            return;
        }

        if (newFactor !== "day") {
            const firstDayInYear = new Date(dateFromRaw.getFullYear(), 0, 1);
            setInput({ ...input, dateFrom: addOneDay(firstDayInYear), dateTo: newDateTo });
            return;
        }

        setInput({ ...input, dateTo: newDateTo });
    }

    const getDateFromLabel = () => {
        const factor = relativeFactorRef.current;
        if (factor === "day") return "Vyberte mesiac";
        if (factor === "year") return "Vyberte rok od";
        return "Vyberte rok";
    }


    type GetSharedSelectPropsParams = {
        id: string;
        label: string;
        value: number[];
        onClear: () => void;
        onChange: (e: SelectChangeEvent<number[]>) => void;
    }
    const getSharedSelectProps = ({ id, label, value, onClear, onChange }: GetSharedSelectPropsParams): SelectProps<number[]> => {
        return {
            multiple: true,
            inputProps: { width: 100 },
            id,
            labelId: `${id}-select`,
            label,
            value: value,
            onChange,
            input: <OutlinedInput label={label} placeholder={label} />,
            endAdornment: value.length > 0 ? <InputAdornment sx={{ marginRight: "10px" }} position="end">
                <IconButton onClick={onClear}>
                    <ClearIcon></ClearIcon>
                </IconButton>
            </InputAdornment> : false
        };
    }

    type GetSelectFormControlParams = GetSharedSelectPropsParams & {
        sx: SxProps;
        options: { value: number; label: string }[];
    }
    const getSelectFormControl = ({ sx, options, ...selectParams }: GetSelectFormControlParams) => {
        return <FormControl sx={sx}>
            <InputLabel id={selectParams.id}>{selectParams.label}</InputLabel>
            <Select {...getSharedSelectProps(selectParams)}>
                {options.map((option) => (
                    <MenuItem
                        key={option.value}
                        value={option.value}
                        style={{ ...getStyles(option.value, selectParams.value, theme), maxWidth: 360, overflow: "hidden" }}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    }

    const getDatetepickerDateTo = (currentDateTo: dayjs.Dayjs): dayjs.Dayjs => {
        const date = new Date(currentDateTo.toDate());
        date.setDate(date.getDate() - 1);
        return dayjs(date);
    }

    return <StyledStatisticsHeader>
        {getSelectFormControl({
            id: "source-type",
            label: "Podľa podavaťeľa",
            value: input.sourceTypes,
            onClear: () => {
                resolveAvailableRequestTypes([]);
                setInput({ ...input, sourceTypes: [] })
            },
            onChange: (e: SelectChangeEvent<number[]>) => {
                const { value } = e.target;
                if (typeof value === "string") return;
                resolveAvailableRequestTypes(value);
                setInput({
                    ...input,
                    sourceTypes: value,
                    requestTypes: availableRequestTypes.current.length > 0
                        ? input.requestTypes.filter(rt => availableRequestTypes.current.includes(rt))
                        : input.requestTypes
                });
            },
            sx: { width: 200 },
            options: sourceTypesOptions,
        })}

        {getSelectFormControl({
            id: "processing-type",
            label: "Podľa spôsobu spracovania",
            value: input.processingTypes,
            onClear: () => setInput({ ...input, processingTypes: [] }),
            onChange: (e: SelectChangeEvent<number[]>) => {
                const { value } = e.target;
                if (typeof value === "string") return;
                setInput({ ...input, processingTypes: value });
            },
            sx: { width: "100%", maxWidth: 245, minWidth: 200 },
            options: processingOptions,
        })}

        {getSelectFormControl({
            id: "requests-types",
            label: "Podľa typu žiadosti",
            value: input.requestTypes,
            onClear: () => setInput({ ...input, requestTypes: [] }),
            onChange: (e: SelectChangeEvent<number[]>) => {
                const { value } = e.target;
                if (typeof value === "string") return;
                setInput({ ...input, requestTypes: value });
            },
            sx: { width: "100%", maxWidth: 360, minWidth: 240 },
            options: typeOptions,
        })}

        <Autocomplete
            disableClearable
            sx={{ width: 200 }}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            options={relativeFactorOptions}
            value={selectedFactorOption}
            onChange={(_e, selectedOption) => {
                if (!selectedOption) return;
                onFactorChange(selectedOption.value);
            }}
            renderInput={(inheritedProps) => <TextField
                {...inheritedProps}
                variant="filled"
                label="Obdobie podľa"
                sx={{ ".MuiInputBase-root": { backgroundColor: "transparent" } }}
            />}
        />

        <Box sx={{ ...getYCenterStyles(), gap: 1, minWidth: 200 }}>
            <DatePicker
                {...sharableProps}
                views={relativeFactorRef.current === "day" ? ["month", "year"] : ["year"]}
                label={getDateFromLabel()}
                maxDate={relativeFactorRef.current === "year" ? dateTo : dayjs(today)}
                value={dateFrom}
                onChange={onDateFromChange}
            />

            {relativeFactorRef.current === "year" && (
                <DatePicker
                    {...sharableProps}
                    views={["year"]}
                    label="Vyberte rok do"
                    value={getDatetepickerDateTo(dateTo)}
                    maxDate={dayjs(lastDateInYear)}
                    minDate={dayjs(dateFromFirstDay)}
                    onChange={(value: dayjs.Dayjs | null) => {
                        if (!value) return;
                        const endOfYear = new Date(value.toDate().getFullYear(), 11, 31);
                        setInput({ ...input, dateTo: endOfYear });
                    }}
                />
            )}
        </Box>
    </StyledStatisticsHeader>
}

type AutomatizationSliderChartProps = {
    processing: StatisticsProcessingOutput | null;
}

const AutomatizationSliderChart = ({ processing }: AutomatizationSliderChartProps) => {
    const theme = useTheme();

    const { configurations } = useAppProvider();

    /// In BE we should use relative colors like "success", "warning" etc. and then map them to the actual color.
    /// Because we need contrastText for the color.
    const getPalleteOptionColor = (color?: string): SimplePaletteColorOptions | undefined => {
        if (!color) return undefined;

        if (color === "#88A926") return theme.palette.success;

        return {
            main: "",
            contrastText: theme.palette.text.primary,
        };
    }

    const dataset: (SliderChartData & { id: number })[] = (processing?.items || [])
        .map((item: BaseStatisticsOutputItem) => {
            const configurationItem = configurations.processing.items.find((configItem) => configItem.id === item.id);

            return {
                percentage: item.percentage,
                color: getPalleteOptionColor(configurationItem?.color),
                icon: configurationItem?.color === "#88A926" ? <IoIosSettings color={theme.palette.success.contrastText} size={20} /> : undefined,
                label: configurationItem?.label || "Neznámy stav",
                count: item.value,
                id: item.id,
            }
        })
        .filter((item) => item.percentage > 0)
        .sort((a, b) => b.id - a.id)

    const onlyMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

    return (
        <>
            {!!dataset.length && onlyMediumScreen && (
                <StyledCardsWrapper>
                    <Typography variant="h6" sx={{ paddingX: { xs: 2, md: 0 }, display: { xs: "inline-block", md: "none" } }}>
                        Podľa spôsobu spracovania
                    </Typography>

                    <StyledCardsScrollableBox>
                        {dataset.map((item, index) => (
                            <StatisticsCard
                                key={index}
                                percentage={item.percentage}
                                percentageColor={item.color?.main ?? theme.palette.text.primary}
                                title={item.label}
                                count={item.count}
                            />
                        ))}
                    </StyledCardsScrollableBox>
                </StyledCardsWrapper>
            )}

            {!!dataset.length && !onlyMediumScreen && <SliderChart dataset={dataset} />}
        </>
    );
}

type TotalCountOfRequestsProps = {
    overall: StatisticsOverallOutput | null;
}

const TotalCountOfRequests = ({ overall }: TotalCountOfRequestsProps) => {
    const theme = useTheme();

    const isPositive = (overall?.compareRatio || 0) > 0;

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            padding: { xs: 2, md: 0 },
            borderBottom: { xs: `1px solid ${theme.palette.divider}`, md: "none" },
            width: { xs: "100%", md: "fit-content" },
        }}>
            <Typography variant="h6">Celkový počet žiadostí</Typography>

            <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1,
            }}>
                <Typography variant="h2" lineHeight={1}>{formatNumber(overall?.value || 0)}x</Typography>

                {!!overall?.compareRatio && (
                    <Chip
                        color={isPositive ? "success" : "error"}
                        label={formatPercent(overall.compareRatio / 100)}
                        size="small"
                        icon={isPositive ? <IoIosTrendingUp /> : <IoIosTrendingDown />}
                    />
                )}
            </Box>

            {!!overall?.compareDateFrom && !!overall?.compareDateTo && !!overall?.compareValue && (
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                }}>
                    <Typography color={theme.palette.text.disabled}>
                        <Box component="span" sx={{ display: "flex", gap: 1, fontWeight: 500 }}>
                            <span>vs predchádajúce {formatNumber(overall.compareValue)}x</span>
                            <Box component="span">
                                {formatSeasons(new Date(overall.compareDateFrom), new Date(overall.compareDateTo))}
                            </Box>
                        </Box>
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

type SourceChart = {
    source: StatisticsSourceOutput | null;
    input: StatisticsInput;
    relativeFactorRef: MutableRefObject<RelativeFactor>;
}

const SourceChart = ({ source, input, relativeFactorRef }: SourceChart) => {
    const theme = useTheme();

    const { configurations } = useAppProvider();

    const { openSnackbar } = SnackbarProvider.useSnackbar();

    const [sourceItems, setSourceItems] = useState(source?.items || []);

    const [statusesRef, _, setStatusesAndRerender] = useControlledState<number[]>([]);

    const data: SourceTotalPercentChartItemProps[] = configurations.source.items
        .map((item) => {
            const foundItem = sourceItems.find((sourceItem) => sourceItem.id === item.id);

            return {
                label: item.label,
                count: foundItem?.value || 0,
                percentage: foundItem?.percentage || 0,
                color: item.color,
            };
        })
        .filter((item) => item.count > 0)
        .sort((a, b) => b.count - a.count);

    const onlyMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

    const relativeFactor = relativeFactorRef.current;

    const getFromTo = (): [Date, Date] => {
        const from = input.dateFrom;
        let to = input.dateTo;

        const today = new Date();

        if (to.getTime() > today.getTime()) to = today;

        return [from, to];
    }

    const getXAxisData = (): number[] => {
        const [from, to] = getFromTo();

        let count = 0;

        if (relativeFactor === "day") count = getDaysCountBetweenDates(from, to) + 1;

        if (relativeFactor === "year") {
            count = to.getFullYear() - from.getFullYear() + 1;
        }

        if (relativeFactor === "month") count = 12;

        if (relativeFactor === "quarter") count = 4;

        return Array.from({ length: count }, (_, i) => i + 1);
    }

    const xAxisData = getXAxisData();

    const xAxisFormatter = (value: number) => {
        if (relativeFactor === "day") {
            const isFirstItem = xAxisData[0] === value || value === 0;

            const isLastItem = xAxisData[xAxisData.length - 1] === value;

            if (isFirstItem || isLastItem) {
                const date = new Date(input.dateFrom);
                date.setDate(value);
                return formatDate(date, "D. MMM");
            }

            return `${value}.`;
        }

        if (relativeFactor === "month") {
            const date = new Date(input.dateFrom);
            date.setMonth(date.getMonth() + value - 1);
            return formatDate(date, "MMM");
        }

        if (relativeFactor === "quarter") {
            return `Q${value}`;
        }

        const date = new Date(input.dateFrom);
        date.setFullYear(date.getFullYear() + (value - 1));
        return formatDate(date, "YYYY");
    }

    const lineChartDatasets: MakeOptional<LineSeriesType, 'type'>[] = configurations.source.items
        .map((configurationItem) => {
            const sourceItem = sourceItems.find((sourceItem) => sourceItem.id === configurationItem.id)

            const breakdown: Record<string, number> = sourceItem?.breakdown || {};

            const fullifiedData = xAxisData.map((xValue: number) => {
                if (relativeFactor === "day") {
                    const date = new Date(input.dateFrom)
                    date.setDate(xValue);
                    const breakdownKey = formatDate(date, "YYYY-MM-DD");
                    return breakdown[breakdownKey] || 0;
                }

                if (relativeFactor === "month") {
                    const date = new Date(input.dateFrom)
                    date.setMonth(date.getMonth() + (xValue - 1));

                    const keys = Object.keys(breakdown).filter((key) => key.startsWith(formatDate(date, "YYYY-MM")));

                    return keys.reduce((acc, key) => acc + breakdown[key], 0);
                }

                if (relativeFactor === "quarter") {
                    const keys = Object.keys(breakdown).filter((key) => {
                        const fromMonth = (xValue - 1) * 3 + 1;
                        const toMonth = xValue * 3;
                        const month = parseInt(key.split("-")[1]);
                        return fromMonth <= month && month <= toMonth;
                    });
                    return keys.reduce((acc, key) => acc + breakdown[key], 0);
                }

                if (relativeFactor === "year") {
                    const date = new Date(input.dateFrom)
                    date.setFullYear(date.getFullYear() + (xValue - 1));
                    const keys = Object.keys(breakdown).filter((key) => key.startsWith(formatDate(date, "YYYY")));
                    return keys.reduce((acc, key) => acc + breakdown[key], 0);
                }

                return 0;
            });

            return {
                data: fullifiedData,
                label: configurationItem.label,
                color: configurationItem.color,
            };
        })
        .filter((dataset) => dataset.data.some((value) => value > 0));

    const { debouncedPerform, isRunning } = useTask(async () => {
        try {
            const sourceInput = StatisticsSourceInputSchema.parse({
                stateTypes: statusesRef.current,
                dateFrom: input.dateFrom,
                dateTo: input.dateTo,
                requestTypes: input.requestTypes,
                sourceTypes: input.sourceTypes,
                processingTypes: input.processingTypes,
            });
            const response = await getSourceStatistics(sourceInput);
            setSourceItems(response.items || []);
        } catch (err) {
            openSnackbar({
                text: "Nepodarilo sa načítať štatistiky podľa statusu",
                type: SnackbarType.ERROR,
            })
        }
    });

    useChangeEffect(() => {
        setStatusesAndRerender([]);
        setSourceItems(source?.items || []);
    }, [source]);

    return (
        <>
            {onlyMediumScreen && (
                <StyledCardsWrapper hideBorder>
                    <StyledCardsScrollableBox>
                        {data.map((item, index) => (
                            <StatisticsCard
                                key={index}
                                percentage={item.percentage}
                                percentageColor={item.color}
                                title={item.label}
                                count={item.count}
                            />
                        ))}
                    </StyledCardsScrollableBox>
                </StyledCardsWrapper>
            )}
            <Box sx={{ paddingX: { xs: 2, md: 0 } }}>
                <Box
                    sx={{
                        width: "100%",
                        borderRadius: `${theme.shape.borderRadius * 2}px`,
                        backgroundColor: theme.palette.grey[100],
                        padding: 0,
                    }}
                >
                    {!onlyMediumScreen && (
                        <>
                            <Box sx={{ paddingX: 2, paddingTop: 2, display: "flex", justifyContent: "flex-end" }}>
                                <SourceChartFilter
                                    isRunning={isRunning}
                                    statuses={statusesRef.current}
                                    setStatuses={(newStatuses: number[]) => {
                                        setStatusesAndRerender(newStatuses);
                                        debouncedPerform();
                                    }}
                                />
                            </Box>

                            <SourceTotalPercentChart data={data} />
                        </>
                    )}

                    <LineChart
                        sx={{
                            ".MuiChartsAxis-line": {
                                visibility: "hidden",
                            },
                            ".MuiChartsGrid-line": {
                                stroke: theme.palette.grey[400],
                            },
                            ".MuiChartsAxis-root": {
                                ".MuiChartsAxis-tick": {
                                    stroke: theme.palette.grey[400],
                                },
                                ".MuiChartsAxis-tickLabel": {
                                    fontSize: "14px!important",
                                }
                            },
                        }}
                        height={600}
                        slotProps={{ legend: { hidden: true } }}
                        xAxis={[{
                            data: xAxisData,
                            valueFormatter: (value, _context) => xAxisFormatter(value),
                            tickInterval: xAxisData,
                        }]}
                        series={lineChartDatasets}
                        grid={{ horizontal: true }}
                    />
                </Box>
            </Box>
        </>
    );
}

type SourceChartFilterProps = {
    statuses: number[];
    setStatuses: (newValues: number[]) => void;
    isRunning: boolean;
}

const SourceChartFilter = (props: SourceChartFilterProps) => {
    const theme = useTheme();

    const { configurations } = useAppProvider();

    const statusOptions = configurations.state.commonItems;

    const parseValue = (event: SelectChangeEvent<string | number[]>): number[] => {
        let value = event.target.value;
        if (typeof value === 'string') value = value.split(',').map((item) => parseInt(item)).filter((item) => !isNaN(item));
        return value;
    }

    const getSelectProps = (value: number[], setter: (value: number[]) => void, labelId: string, label: string): SelectProps<number[]> => {
        const clearButton: JSX.Element = (
            <InputAdornment sx={{ marginRight: "10px" }} position="end">
                <IconButton onClick={() => { setter([]); }}>
                    <ClearIcon></ClearIcon>
                </IconButton>
            </InputAdornment>
        );

        return {
            labelId: labelId,
            id: `${labelId}-select`,
            multiple: true,
            value,
            label,
            placeholder: label,
            onChange: (e: SelectChangeEvent<number[]>) => {
                const newValue = parseValue(e);
                setter(newValue);
            },
            endAdornment: value.length > 0 ? clearButton : false,
            input: (
                <OutlinedInput label={label} placeholder={label} />
            )
        };
    }

    return (
        <Box sx={{ ...getYCenterStyles(), justifyContent: "flex-end", gap: 1 }}>
            {props.isRunning && <CircularProgress size={24} />}

            <FormControl sx={{ width: 300 }}>
                <InputLabel id="statuses-types">Podľa stavu žiadosti</InputLabel>
                <Select {...getSelectProps(props.statuses, props.setStatuses, "statuses-types", "Podľa stavu žiadosti")}>
                    {statusOptions.map((option) => (
                        <MenuItem
                            key={option.id}
                            value={option.id}
                            style={getStyles(option.id, props.statuses, theme)}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );
}

type SourceTotalPercentChartProps = {
    data: SourceTotalPercentChartItemProps[];
}

const SourceTotalPercentChart = (props: SourceTotalPercentChartProps) => {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, padding: 2, paddingBottom: 0 }}>
            {props.data.map((item, index) => (
                <SourceTotalPercentChartItem key={index} {...item} />
            ))}
        </Box>
    );
}

type SourceTotalPercentChartItemProps = {
    label: string;
    count: number;
    percentage: number;
    color: string;
}

const SourceTotalPercentChartItem = (props: SourceTotalPercentChartItemProps) => {
    const theme = useTheme();

    const verticalAlign = { display: "flex", alignItems: "center" };

    return (
        <Box sx={{ ...verticalAlign, gap: 1.5, width: "100%" }}>
            <TextOverflow
                variant="body2"
                width="85px"
                color={theme.palette.text.primary}
                lineHeight={1}
                fontWeight={600}
                text={props.label}
                fontSize={14}
            />

            <Box sx={{
                ...verticalAlign,
                justifyContent: "space-between",
                height: 30,
                borderRadius: "30px",
                backgroundColor: props.color,
                width: `${props.percentage}%`,
                padding: 1.5,
                minWidth: 45,
            }}>
                <Typography variant="body2" fontSize={14} color={theme.palette.common.white} lineHeight={1}>
                    {formatNumber(props.count)}x
                </Typography>
            </Box>

            <Typography fontWeight={500} fontSize={15} color={props.color}>
                {formatPercent(props.percentage / 100)}
            </Typography>
        </Box>
    );
}

const StyledStatisticsHeader = ({ children }: { children: React.ReactNode }) => {
    return (
        <Box sx={(theme) => ({
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: 2,
            gap: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexWrap: "wrap",
        })}>
            {children}
        </Box>
    )
}

const StyledRequestCountWrapper = ({ children }: { children: React.ReactNode }) => {
    return (
        <Box sx={{
            display: "flex",
            width: "100%",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: { xs: "flex-start", md: "space-between" },
            padding: 2,
            paddingX: { xs: 0, md: 2 },
            flexWrap: "wrap",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
        }}>
            {children}
        </Box>
    );
}

const StyledCardsWrapper = ({ children, hideBorder }: { children: React.ReactNode; hideBorder?: boolean }) => {
    return (
        <Box sx={(theme) => ({
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: { xs: "flex-start", md: "flex-end" },
            gap: 2,
            width: { xs: "100%", md: "fit-content" },
            overflow: "hidden",
            paddingBottom: { xs: 2, md: 0 },
            borderBottom: hideBorder ? "none" : { xs: `1px solid ${theme.palette.divider}`, md: "none" },
        })}>
            {children}
        </Box>
    );
}

const StyledCardsScrollableBox = ({ children }: { children: React.ReactNode }) => {
    return (
        <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, md: 2 },
            width: { xs: "100%", md: "fit-content" },
            overflowX: { xs: "auto", md: "visible" },
            paddingX: { xs: 2, md: 0 },
            [`&::-webkit-scrollbar`]: { display: "none" },
        }}>
            {children}
        </Box>
    );
}

const StatisticsSkeleton = () => {
    return (
        <ContentWrapper>
            <StyledStatisticsHeader>
                <Skeleton variant="text" width={200} height={40} />
            </StyledStatisticsHeader>

            <StyledRequestCountWrapper>
                <Skeleton variant="rounded" width={300} height={100} sx={{ marginLeft: { xs: 2, md: 0 } }} />

                <StyledCardsWrapper>
                    <StyledCardsScrollableBox>
                        <Skeleton variant="rounded" width={200} height={100} />

                        <Skeleton variant="rounded" width={200} height={100} />

                        <Skeleton variant="rounded" width={200} height={100} />
                    </StyledCardsScrollableBox>
                </StyledCardsWrapper>
            </StyledRequestCountWrapper>

            <Box sx={{
                width: "100%",
                display: "flex",
                flexWrap: { xs: "wrap", lg: "nowrap" },
                gap: 2,
                paddingX: { xs: 0, md: 2 },
                marginBottom: 10,
            }}>
                <Box sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    paddingX: { xs: 2, md: 0 },
                }}>
                    <Skeleton variant="rounded" height={50} />

                    <Box sx={{ width: "100%" }}>
                        <Skeleton variant="rounded" height={625} />
                    </Box>
                </Box>

                <Box sx={{
                    display: "flex",
                    maxWidth: { xs: "100%", lg: 400 },
                    width: "100%",
                    flexShrink: { xs: 1, lg: 0 },
                    paddingX: { xs: 2, md: 0 },
                }}>
                    <Skeleton variant="rounded" width="100%" height={692} />
                </Box>
            </Box>
        </ContentWrapper>
    );
};
